import { Conversation as ConversationsSDKConversation, Message, MessageBuilder } from "@twilio/conversations";
import {
    Conversation,
    MessagesDirection,
    SendEmailMessageOptions,
    SendMediaMessageOptions,
    SendTextMessageOptions
} from "./Conversation";
import { convertHTMLToPlainText } from "~/modules/Conversation/ConversationUtils";
import { ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { Paginator } from "~/modules/actions/Paginator";
import { createFlexSdkError, throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";





export type SendMessageOptions = SendTextMessageOptions | SendEmailMessageOptions | SendMediaMessageOptions;

export class ConversationImpl implements Conversation {
    readonly #conversation: ConversationsSDKConversation;

    readonly #ctx: ContextManager;

    readonly #logger: Logger;

    constructor(ctx: ContextManager, conversation: ConversationsSDKConversation) {
        this.#conversation = conversation;
        this.#ctx = ctx;
        this.#logger = getLogger(ctx)(LoggerName.Conversations);
    }

    get sid() {
        return this.#conversation.sid;
    }

    get conversation() {
        return this.#conversation;
    }

    private validateConversation(operation: string): void {
        if (!this.#conversation) {
            const errorMsg = `${operation}: Conversation is not available`;
            this.#logger.error(errorMsg);
            throwFlexSdkError(this.#ctx)(
                ErrorCode.ConversationNotAvailable,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }
    }

    private getMessageBody(messageOptions: SendMessageOptions): { body: string; hasContent: boolean } {
        const isHTMLMessage = Boolean("htmlBody" in messageOptions && messageOptions.htmlBody);
        let plainTextBody = "";

        if (isHTMLMessage && !("body" in messageOptions && messageOptions.body) && "htmlBody" in messageOptions) {
            plainTextBody = convertHTMLToPlainText(messageOptions.htmlBody);
        }

        const body = "body" in messageOptions && messageOptions.body ? messageOptions.body : plainTextBody;
        return { body, hasContent: body.length > 0 };
    }

    private addMediaToMessage(preparedMessage: MessageBuilder, attachedFiles?: File[]): boolean {
        if (!attachedFiles || attachedFiles.length === 0) return false;

        attachedFiles.forEach((file: File) => {
            const formData = new FormData();
            formData.append(file.name, file);
            preparedMessage.addMedia(formData);
        });
        return true;
    }

    private addEmailContent(
        preparedMessage: MessageBuilder,
        messageOptions: SendMessageOptions,
        plainTextBody: string
    ): boolean {
        let hasContent = false;

        if ("subject" in messageOptions && messageOptions.subject) {
            preparedMessage.setSubject(messageOptions.subject);
            hasContent = true;
        }

        if ("htmlBody" in messageOptions && messageOptions.htmlBody && messageOptions.htmlBody !== "") {
            preparedMessage.setEmailBody("text/html", {
                media: messageOptions.htmlBody,
                contentType: "text/html"
            });
            hasContent = true;
        }

        if (plainTextBody && plainTextBody !== "") {
            preparedMessage.setEmailBody("text/plain", {
                media: plainTextBody,
                contentType: "text/plain"
            });
            hasContent = true;
        }

        return hasContent;
    }

    async sendMessage(messageOptions: SendMessageOptions): Promise<number | null> {
        try {
            this.validateConversation("sendMessage");

            const { body, hasContent: bodyHasContent } = this.getMessageBody(messageOptions);
            let messageHasContent = bodyHasContent;

            const preparedMessage = this.conversation.prepareMessage().setBody(body);

            const hasMedia = this.addMediaToMessage(preparedMessage, messageOptions.attachedFiles);
            messageHasContent = messageHasContent || hasMedia;

            if (messageOptions.messageAttributes) {
                
                preparedMessage.setAttributes(messageOptions.messageAttributes);
            }

            
            const isEmailMessage = "htmlBody" in messageOptions || "subject" in messageOptions;
            if (isEmailMessage) {
                const hasEmailContent = this.addEmailContent(preparedMessage, messageOptions, body);
                messageHasContent = messageHasContent || hasEmailContent;
            }

            if (!messageHasContent) {
                const errorMsg = "sendMessage: Cannot send an empty message";
                this.#logger.error(errorMsg);
                return Promise.reject(
                    createFlexSdkError(this.#ctx)(
                        ErrorCode.MessageContentMissing,
                        { severity: ErrorSeverity.Error },
                        errorMsg
                    )
                );
            }

            return await preparedMessage.build().send();
        } catch (error) {
            if (error instanceof FlexSdkError) throw error;

            const errorMsg = `sendMessage: Failed to send message`;
            this.#logger.error(errorMsg, error);
            return Promise.reject(
                createFlexSdkError(this.#ctx)(
                    ErrorCode.FailedToSendMessage,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }

    async sendTyping(): Promise<void> {
        this.validateConversation("sendTyping");

        try {
            await this.#conversation.typing();
        } catch (error) {
            const errorMsg = `sendTyping: Failed to send typing indicator`;
            this.#logger.error(errorMsg, error);
            throwFlexSdkError(this.#ctx)(
                ErrorCode.FailedToSendTypingIndicator,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }
    }

    async getMessages(
        pageSize?: number,
        anchorMessageIndex?: number,
        direction?: MessagesDirection
    ): Promise<Paginator<Message>> {
        this.validateConversation("getMessages");

        try {
            return this.#conversation.getMessages(pageSize, anchorMessageIndex, direction);
        } catch (error) {
            const errorMsg = `getMessages: Failed to get messages`;
            this.#logger.error(errorMsg, error);
            return throwFlexSdkError(this.#ctx)(
                ErrorCode.FailedToRetrieveMessages,
                { severity: ErrorSeverity.Error },
                errorMsg,
                error
            );
        }
    }
}
